import {isArray, isFunction} from 'lodash-es';
import {
    NavigationGuardNext,
    RouteLocationNormalized,
    RouteRecordRaw,
    Router,
    createRouter,
    createWebHashHistory
} from 'vue-router';
import {APP_EMBEDDED} from '../../app.const';
import {useService} from '../../core/useService';
import {Role, UserRecord} from '../../records/user.record';
import {useEvoIntegration} from '../planning-tool/composable/use-evo-integration';
import {useAppContext} from './AppContext';
import dispatcherSettingsRoutes from './routes/dispatcher-settings.routes';
import driverFormRoutes from './routes/driver-form.routes';
import orderFilterRoutes from './routes/order-filter.routes';
import orderFormRoutes from './routes/order-form.routes';
import reportsRoutes from './routes/reports.routes';
import {RouteDataService} from './routes/route-data.service';
import settingsRoutes from './routes/settings.routes';
import vehicleDriverFilterRoutes from './routes/vehicle-driver-filter.routes';
import vehicleFormRoutes from './routes/vehicle-form.routes';

export class RoutesService {
    private router: Router;

    constructor(private routeDataService: RouteDataService = useService(RouteDataService)) {
        this.router = createRouter({
            linkActiveClass: 'active',
            routes: [],
            history: createWebHashHistory(location.origin)
        });

        this.router.beforeEach(
            (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext): void => {
                const user = useAppContext().user;
                if (!user) {
                    next();
                    return;
                }
                const dispatcherNoAoc =
                    user.currentRole === Role.DISPATCHER && (!user.dispatcherAoc || !user.dispatcherAoc.length);

                const CustomerServiceNoAoc =
                    user.currentRole === Role.CUSTOMER_SERVICE && (!user.csAoc || !user.csAoc.length);
                const noAoc = dispatcherNoAoc || CustomerServiceNoAoc;
                if (noAoc && to.path !== '/splash') {
                    next({path: '/splash'});
                    return;
                }
                if (!noAoc && to.path === '/splash') {
                    next({path: this.getDefaultUrl(user)});
                    return;
                }
                let toDefault = false;
                const {matched} = this.router.resolve(to.fullPath);
                if (matched.length === 0) {
                    toDefault = true;
                } else if (
                    to.meta.roles &&
                    isArray(to.meta.roles) &&
                    (to.meta.roles as string[]).indexOf(user.currentRole) === -1
                ) {
                    toDefault = true;
                }
                if (toDefault) {
                    next({path: this.getDefaultUrl(user)});
                    return;
                }
                if (to.path === '/settings') {
                    next({
                        path: '/settings/' + this.filterRoutes(settingsRoutes, user.currentRole)[0].path
                    });
                } else if (to.path === '/reports') {
                    next({
                        path: '/reports/' + this.filterRoutes(reportsRoutes, user.currentRole)[0].path
                    });
                } else {
                    next();
                }
            }
        );

        this.router.afterEach((to: RouteLocationNormalized) => {
            if (to.fullPath != '/login' && to.fullPath != '/logout' && to.fullPath != '/') {
                sessionStorage.setItem('initialURL', to.fullPath);
            }
            if (APP_EMBEDDED()) {
                useEvoIntegration().reportLocation(to.path);
            }
        });
    }

    public filterRoutes(routes: RouteRecordRaw[], role: Role) {
        const settings = useAppContext().settings;
        return routes.reduce((a: RouteRecordRaw[], c: RouteRecordRaw) => {
            if (
                (!c.meta?.access ||
                    (c.meta?.access && isFunction(c.meta.access) && (c.meta as any).access(settings))) &&
                (!c.meta?.roles || (isArray(c.meta?.roles) && (c.meta.roles as string[]).find((r) => r === role)))
            ) {
                if (c.children && c.children.length) {
                    const children: RouteRecordRaw[] = [];
                    if (c.children) {
                        children.push(...this.filterRoutes(c.children, role));
                    }
                    if (children.length > 0) {
                        a.push(c);
                    }
                } else {
                    a.push(c);
                }
            }
            return a;
        }, []);
    }

    addRoutes(role: Role) {
        const mainRoutes = this.filterRoutes(this.getMainRoutes(), role);
        if (mainRoutes.some((r) => r.path === 'splash')) {
            this.router.addRoute({
                path: '/splash',
                name: 'dispatcherSplash',
                component: () => import('../splash/DispatcherSplash.vue'),
                meta: {roles: [Role.DISPATCHER, Role.CUSTOMER_SERVICE]}
            });
        }
        this.router.addRoute({
            path: '/',
            name: 'main',
            component: APP_EMBEDDED() ? () => import('../Embedded.vue') : () => import('../Main.vue'),
            children: mainRoutes.filter((r) => r.path !== 'splash')
        });
    }

    getRouter(): Router {
        return this.router;
    }

    getMainRoutes(): RouteRecordRaw[] {
        return [
            {
                path: 'splash',
                name: 'dispatcherSplash',
                component: () => import('../splash/DispatcherSplash.vue'),
                props: {},
                meta: {roles: [Role.DISPATCHER, Role.CUSTOMER_SERVICE]}
            },
            {
                path: 'planning-tool',
                name: 'planningTool',
                component: () => import('../planning-tool/PlanningTool.vue'),
                props: {},
                children: [
                    ...vehicleFormRoutes('planningTool'),
                    ...orderFormRoutes('planningTool'),
                    ...dispatcherSettingsRoutes('planningTool'),
                    ...driverFormRoutes('planningTool'),
                    ...orderFilterRoutes('planningTool'),
                    ...vehicleDriverFilterRoutes('planningTool'),
                    {
                        path: 'bulk-change',
                        name: 'planningToolBulkChange',
                        component: () => import('../settings/bulk-change/BulkChangeForm.vue'),
                        props: {}
                    }
                ],
                meta: {
                    roles: [Role.DISPATCHER]
                }
            },
            {
                path: 'track-and-trace',
                name: 'trackAndTrace',
                component: () => import('../track-and-trace/TrackAndTrace.vue'),
                props: {},
                children: [
                    ...vehicleFormRoutes('trackAndTrace'),
                    ...orderFormRoutes('trackAndTrace'),
                    ...driverFormRoutes('trackAndTrace'),
                    {
                        path: 'order-action/:orderId/:zoneId',
                        name: 'trackAndTraceOrderAction',
                        component: () => import('../track-and-trace/components/OrderAction.vue'),
                        props: (route) => {
                            return Object.assign(
                                {
                                    orderId: Number(route.params.orderId),
                                    backRouteName: 'trackAndTrace',
                                    zoneId: route.params.zoneId
                                },
                                this.routeDataService.getRouteData(route.query.key as string)
                            );
                        }
                    },
                    {
                        path: 'orders/:zoneId',
                        name: 'trackAndTraceOrders',
                        component: () => import('../track-and-trace/components/Orders.vue'),
                        props: (route) => {
                            return {
                                zoneId: route.params.zoneId,
                                backRouteName: 'trackAndTrace'
                            };
                        },
                        children: [
                            ...orderFormRoutes('trackAndTraceOrders'),
                            {
                                path: 'order-action/:orderId/:zoneId',
                                name: 'trackAndTraceOrdersOrderAction',
                                component: () => import('../track-and-trace/components/OrderAction.vue'),
                                props: (route) => {
                                    return Object.assign(
                                        {
                                            orderId: Number(route.params.orderId),
                                            backRouteName: 'trackAndTraceOrders',
                                            zoneId: route.params.zoneId
                                        },
                                        this.routeDataService.getRouteData(route.query.key as string)
                                    );
                                }
                            }
                        ]
                    },
                    {
                        path: `export`,
                        name: `trackAndTraceExport`,
                        component: () => import('../track-and-trace/components/TrackAndTraceExport.vue'),
                        props: (route) => {
                            return {
                                backRouteName: 'trackAndTrace',
                                runNumber: route.query.runNumber,
                                vehicleId: route.query.vehicleId,
                                date: route.query.date
                            };
                        }
                    },
                    {
                        path: `routes`,
                        name: `trackAndTraceRoutes`,
                        component: () =>
                            import('../track-and-trace/components/track-and-trace-map/TrackAndTraceRoutes.vue'),
                        props: (route) => {
                            return Object.assign(
                                {backRouteName: 'trackAndTrace'},
                                this.routeDataService.getRouteData(route.query.key as string)
                            );
                        }
                    }
                ],
                meta: {
                    roles: [Role.DISPATCHER, Role.CUSTOMER_SERVICE]
                }
            },
            {
                path: 'settings',
                name: 'settings',
                component: () => import('../settings/Settings.vue'),
                props: {},
                children: settingsRoutes,
                meta: {
                    roles: [Role.ADMIN, Role.DISPATCHER]
                }
            },
            {
                path: 'orders',
                name: 'orders',
                component: () => import('../settings/orders/OrdersList.vue'),
                props: {},
                children: orderFormRoutes('orders'),
                meta: {
                    roles: [Role.ADMIN, Role.DISPATCHER]
                }
            },
            {
                path: 'reports',
                name: 'reports',
                component: () => import('../reports/Reports.vue'),
                meta: {
                    roles: [Role.ADMIN, Role.DISPATCHER, Role.CUSTOMER_SERVICE]
                },
                props: {},
                children: reportsRoutes
            }
        ];
    }

    goToDefaultRoute() {
        this.router.push({path: sessionStorage.getItem('initialURL') || '/planning-tool'});
    }

    getDefaultUrl(user: UserRecord, igoroeSaved: boolean = false): string {
        const role = user?.currentRole;
        const dispatcherNoAoc = role === Role.DISPATCHER && (!user.dispatcherAoc || !user.dispatcherAoc.length);
        const CustomerServiceNoAoc = role === Role.CUSTOMER_SERVICE && (!user.csAoc || !user.csAoc.length);
        if (dispatcherNoAoc || CustomerServiceNoAoc) {
            sessionStorage.removeItem('initialURL');
            return '/splash';
        }
        const initalUrl = sessionStorage.getItem('initialURL');
        let url: string = initalUrl != '/' ? initalUrl : null;
        if (igoroeSaved) {
            url = null;
        }
        if (!url) {
            switch (role) {
                case Role.DISPATCHER:
                    url = '/planning-tool';
                    break;
                case Role.ADMIN:
                    url = '/settings';
                    break;
                case Role.CUSTOMER_SERVICE:
                    url = '/track-and-trace';
                    break;
            }
        }
        sessionStorage.removeItem('initialURL');
        return url || '/settings';
    }
}
